import * as React from 'react';
import { useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Alert,
  Skeleton,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { withTranslation } from 'react-i18next';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import { usePerformanceReport } from '../hooks/useReportsApi';
import { useUserStore } from '../../../store';

import { can } from '../../../utils/access';
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '–';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

function HelpTooltip({ title }) {
  return (
    <Tooltip title={title} enterTouchDelay={0} leaveTouchDelay={3000} arrow>
      <IconButton size="small" sx={{ ml: 0.5, p: 0.25 }} tabIndex={0}>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
      </IconButton>
    </Tooltip>
  );
}

function PerformanceSubTab({ t, dateParams }) {
  const { data, loading, error } = usePerformanceReport(dateParams);
  const { currentOrganization } = useUserStore();
  const showCosts = can(currentOrganization, 'employees.view_costs');

  const chartData = useMemo(() => {
    if (!data?.employees) return [];
    const rows = data.employees.map((e) => ({
      name: e.name,
      labor: Number(e.labor_revenue) || 0,
      parts: Number(e.parts_revenue) || 0,
    }));
    if (data.unattributed && (Number(data.unattributed.labor_revenue) > 0 || Number(data.unattributed.parts_revenue) > 0)) {
      rows.push({
        name: t('reports.employees.unattributed'),
        labor: Number(data.unattributed.labor_revenue) || 0,
        parts: Number(data.unattributed.parts_revenue) || 0,
      });
    }
    rows.sort((a, b) => (b.labor + b.parts) - (a.labor + a.parts));
    return rows;
  }, [data, t]);

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <ExportButtons reportType="employees" params={{ ...dateParams, sub_tab: 'performance' }} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={showCosts ? true : 3}>
          <MetricCard
            icon={EngineeringIcon}
            label={t('reports.employees.totalEmployees')}
            value={data?.employee_count}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={showCosts ? true : 3}>
          <MetricCard
            icon={BuildIcon}
            label={t('reports.employees.laborRevenue')}
            value={data?.total_labor}
            loading={loading}
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={showCosts ? true : 3}>
          <MetricCard
            icon={SettingsIcon}
            label={t('reports.employees.partsRevenue')}
            value={data?.total_parts}
            loading={loading}
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={showCosts ? true : 3}>
          <Tooltip title={t('reports.employees.tooltipTotalRevenueCard')} enterTouchDelay={0} leaveTouchDelay={3000} arrow>
            <Box sx={{ height: '100%' }}>
              <MetricCard
                icon={EuroIcon}
                label={t('reports.employees.totalRevenueCard')}
                value={data?.total_revenue}
                loading={loading}
                format="currency"
              />
            </Box>
          </Tooltip>
        </Grid>
        {showCosts && (
          <Grid item xs={12} sm={6} md>
            <MetricCard
              icon={TrendingUpIcon}
              label={t('reports.employees.workMarginCard')}
              value={data?.total_work_margin}
              loading={loading}
              format="currency"
              iconColor={data?.total_work_margin > 0 ? 'success.main' : data?.total_work_margin < 0 ? 'error.main' : 'secondary.main'}
              iconBgColor={data?.total_work_margin > 0 ? 'rgba(16, 185, 129, 0.1)' : data?.total_work_margin < 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(49, 130, 206, 0.1)'}
            />
          </Grid>
        )}
      </Grid>

      {/* Revenue per Employee Chart */}
      {!loading && chartData.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('reports.employees.revenueByEmployee')}
          </Typography>
          <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 44 + 60)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k €` : `${v} €`} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 13 }} />
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, boxShadow: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{d.name}</Typography>
                      <Typography variant="body2">{t('reports.employees.laborRevenue')}: {formatCurrency(d.labor)}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('reports.employees.partsRevenue')}: {formatCurrency(d.parts)}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{t('reports.employees.total')}: {formatCurrency(d.labor + d.parts)}</Typography>
                    </Box>
                  );
                }}
              />
              <Legend />
              <Bar dataKey="labor" name={t('reports.employees.laborRevenue')} stackId="a" fill="#3182ce" />
              <Bar dataKey="parts" name={t('reports.employees.partsRevenue')} stackId="a" fill="#cbd5e1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Performance Table */}
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.employees.performance')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.employees.employee')}</TableCell>
                  <TableCell align="right">
                    {t('reports.employees.workOrders')}
                    <HelpTooltip title={t('reports.employees.tooltipWorkOrders')} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {t('reports.employees.laborRevenue')}
                    <HelpTooltip title={t('reports.employees.tooltipLaborRevenue')} />
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {t('reports.employees.partsRevenue')}
                    <HelpTooltip title={t('reports.employees.tooltipPartsRevenue')} />
                  </TableCell>
                  <TableCell align="right">
                    {t('reports.employees.total')}
                    <HelpTooltip title={t('reports.employees.tooltipTotal')} />
                  </TableCell>
                  {showCosts && (
                    <>
                      <TableCell align="right">
                        {t('reports.employees.monthlyCost')}
                        <HelpTooltip title={t('reports.employees.tooltipMonthlyCost')} />
                      </TableCell>
                      <TableCell align="right">
                        {t('reports.employees.workMargin')}
                        <HelpTooltip title={t('reports.employees.tooltipWorkMargin')} />
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.employees || []).map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500 }}>{employee.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{employee.role}</Typography>
                    </TableCell>
                    <TableCell align="right">{employee.work_orders}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(employee.labor_revenue)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {formatCurrency(employee.parts_revenue)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(employee.total_revenue)}
                    </TableCell>
                    {showCosts && (
                      <>
                        <TableCell align="right">
                          {employee.monthly_cost != null ? formatCurrency(employee.monthly_cost) : '–'}
                        </TableCell>
                        <TableCell align="right">
                          {employee.work_margin != null ? (
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: employee.work_margin >= 0 ? 'success.main' : 'error.main',
                              }}
                            >
                              {formatCurrency(employee.work_margin)}
                            </Typography>
                          ) : '–'}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
                {data?.unattributed && (
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>
                      <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        {t('reports.employees.unattributed')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">–</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(data.unattributed.labor_revenue)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {formatCurrency(data.unattributed.parts_revenue)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(data.unattributed.total_revenue)}
                    </TableCell>
                    {showCosts && (
                      <>
                        <TableCell align="right">–</TableCell>
                        <TableCell align="right">–</TableCell>
                      </>
                    )}
                  </TableRow>
                )}
                {(!data?.employees || data.employees.length === 0) && !data?.unattributed && (
                  <TableRow>
                    <TableCell colSpan={showCosts ? 7 : 5} align="center" sx={{ color: 'text.secondary' }}>
                      {t('reports.noData')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {data?.employees && data.employees.length > 0 && (
                <TableFooter>
                  <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                    <TableCell>{t('reports.employees.totalRow')}</TableCell>
                    <TableCell align="right">{data.distinct_wo_count}</TableCell>
                    <TableCell align="right">{formatCurrency(data.total_labor)}</TableCell>
                    <TableCell align="right">{formatCurrency(data.total_parts)}</TableCell>
                    <TableCell align="right">{formatCurrency(data.total_revenue)}</TableCell>
                    {showCosts && (
                      <>
                        <TableCell align="right" />
                        <TableCell align="right">
                          {data.total_work_margin != null ? formatCurrency(data.total_work_margin) : '–'}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default withTranslation()(PerformanceSubTab);
